import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Pin, PinOff, Plus, Search, StickyNote, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/queries';
import { useDebounced } from '@/hooks/useDebounced';
import { noteKeys, useNotes, type Note, type NoteFilter } from '@/lib/noteQueries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * A private scratchpad on the dashboard.
 *
 * Sits in the learner's own column because that is what it is — nobody else
 * can see these, and nothing in the LMS reads them. Styled from the same
 * tokens as every other card here rather than the reference's own palette, so
 * it reads as part of the page instead of an embedded widget.
 */
export function PersonalNotepad() {
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<NoteFilter>('all');
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const composeRef = useRef<HTMLTextAreaElement>(null);
  const debouncedSearch = useDebounced(search.trim(), 300);
  const { data, isLoading } = useNotes(filter, debouncedSearch);

  // Every mutation touches the same list, and the counts on the tabs move with
  // it — so they all invalidate the whole tree rather than one filtered key.
  const refresh = () => queryClient.invalidateQueries({ queryKey: noteKeys.all() });

  const create = useApiMutation({
    mutationFn: async (content: string) => {
      const { data: note } = await api.post<Note>('/notes', { content });
      return note;
    },
    onSuccess: () => {
      setDraft('');
      setComposing(false);
      void refresh();
    },
  });

  const update = useApiMutation({
    mutationFn: async (input: {
      id: string;
      content?: string;
      isPinned?: boolean;
      completed?: boolean;
    }) => {
      const { id, ...body } = input;
      const { data: note } = await api.patch<Note>(`/notes/${id}`, body);
      return note;
    },
    onSuccess: () => {
      setEditingId(null);
      void refresh();
    },
  });

  const remove = useApiMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/notes/${id}`);
    },
    successMessage: 'Note deleted',
    onSuccess: () => void refresh(),
  });

  const notes = data?.items ?? [];
  const counts = data?.meta;

  const startEdit = (note: Note) => {
    setEditingId(note.id);
    setEditDraft(note.content);
  };

  const submitCompose = () => {
    const content = draft.trim();
    if (content) create.mutate(content);
  };

  return (
    /*
      ── A FIXED HEIGHT, WHATEVER IS IN IT ─────────────────────────────────

      The card used to be as tall as its contents, so it grew with every note
      and shrank back when one was ticked off — and because it sits at the
      foot of a dashboard column, everything around it moved each time. On an
      empty notepad it was short; on a dozen notes it ran past the fold.

      The height is the card's now, not the list's. The header, the tabs and
      the composer stay put; only the NOTES scroll. Writing a note, deleting
      one or filtering the list leaves the page exactly as it was.

      `h-[26rem]` rather than a min or a max: both of those still let one end
      move. This is the one measurement that makes the card the same size on
      every screen it appears on and in every state it can be in.
    */
    <Card className="flex h-[26rem] flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b px-5 py-4">
        <StickyNote className="h-[18px] w-[18px] shrink-0 text-muted-foreground" aria-hidden />
        <h2 className="text-[15px] font-semibold">Personal notepad</h2>
        <span className="text-[13px] text-muted-foreground">Only you can see these</span>

        <Button
          size="sm"
          className="ml-auto"
          onClick={() => {
            setComposing(true);
            // Focus after the textarea exists, so clicking New note lands the
            // cursor in it rather than needing a second click.
            requestAnimationFrame(() => composeRef.current?.focus());
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          New note
        </Button>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b px-5 py-3">
        {/* Tabs, matching My Courses: underline with a mono count. */}
        <div className="flex items-end gap-1">
          {(['all', 'completed'] as NoteFilter[]).map((option) => (
            <button
              key={option}
              onClick={() => setFilter(option)}
              aria-pressed={filter === option}
              className={cn(
                '-mb-3 border-b-2 px-2 pb-3 pt-1 text-[13px] font-medium capitalize transition-colors',
                filter === option
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {option}
              {counts && (
                <span className="ml-1.5 font-mono text-[11px] text-muted-foreground/70">
                  {option === 'all' ? counts.total : counts.completed}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative ml-auto w-full sm:w-56">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes"
            aria-label="Search notes"
            className="h-9 rounded-lg pl-9 text-[13px]"
          />
        </div>
      </div>

      {composing && (
        <div className="shrink-0 border-b bg-muted/30 px-5 py-4">
          <Textarea
            ref={composeRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a note to yourself…"
            rows={3}
            className="resize-none text-[13.5px]"
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter saves, Escape abandons — the pair anybody
              // expects from a box that opened over what they were reading.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitCompose();
              if (e.key === 'Escape') {
                setComposing(false);
                setDraft('');
              }
            }}
          />
          <div className="mt-2.5 flex items-center gap-2">
            <Button size="sm" loading={create.isPending} onClick={submitCompose} disabled={!draft.trim()}>
              Save note
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setComposing(false);
                setDraft('');
              }}
            >
              Cancel
            </Button>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground/70">⌘↵</span>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="flex min-h-0 flex-1 items-center justify-center px-5 text-center text-sm text-muted-foreground">
          Loading notes…
        </p>
      ) : notes.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-6 text-center">
          <StickyNote className="mb-3 h-8 w-8 text-muted-foreground/40" aria-hidden />
          <p className="text-sm font-medium">
            {/* Three different empty states, because "no notes" is wrong when
                the list is only empty because of a search or a tab. */}
            {debouncedSearch
              ? 'Nothing matches that'
              : filter === 'completed'
                ? 'Nothing ticked off yet'
                : 'No notes yet'}
          </p>
          <p className="mt-1 max-w-xs text-[13px] text-muted-foreground">
            {debouncedSearch
              ? 'Try a different word, or clear the search.'
              : filter === 'completed'
                ? 'Notes you tick off will collect here.'
                : 'Keep a reminder, a question for your manager, or anything from a session.'}
          </p>
          {!debouncedSearch && filter === 'all' && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => {
                setComposing(true);
                requestAnimationFrame(() => composeRef.current?.focus());
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Write your first note
            </Button>
          )}
        </div>
      ) : (
        /* The only part that scrolls. `min-h-0` is what lets it: a flex child
           defaults to a minimum of its content, which would push the card
           taller instead of scrolling inside it. */
        <ul className="min-h-0 flex-1 divide-y overflow-y-auto scrollbar-thin">
          {notes.map((note) => {
            const isCompleted = note.completedAt !== null;
            const isEditing = editingId === note.id;

            return (
              <li key={note.id} className={cn('px-5 py-3.5', isCompleted && 'bg-muted/25')}>
                {isEditing ? (
                  <div>
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      autoFocus
                      className="resize-none text-[13.5px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          update.mutate({ id: note.id, content: editDraft.trim() });
                        }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                    <div className="mt-2.5 flex gap-2">
                      <Button
                        size="sm"
                        loading={update.isPending}
                        disabled={!editDraft.trim()}
                        onClick={() => update.mutate({ id: note.id, content: editDraft.trim() })}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => update.mutate({ id: note.id, completed: !isCompleted })}
                      aria-label={isCompleted ? 'Mark as not done' : 'Mark as done'}
                      aria-pressed={isCompleted}
                      className={cn(
                        'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                        isCompleted
                          ? 'border-success bg-success text-white'
                          : 'border-muted-foreground/40 hover:border-foreground',
                      )}
                    >
                      {isCompleted && <Check className="h-3 w-3" strokeWidth={3} />}
                    </button>

                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'whitespace-pre-wrap break-words text-[13.5px] leading-relaxed',
                          isCompleted && 'text-muted-foreground line-through',
                        )}
                      >
                        {note.content}
                      </p>
                      <p className="mt-1 font-mono text-[10.5px] text-muted-foreground/70">
                        {note.isPinned && 'Pinned · '}
                        {new Date(note.updatedAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </p>
                    </div>

                    {/* Revealed on hover, but always reachable by keyboard —
                        `focus-within` keeps them from vanishing under a tab. */}
                    <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 sm:opacity-60 sm:hover:opacity-100">
                      <IconButton
                        label={note.isPinned ? 'Unpin note' : 'Pin note'}
                        onClick={() => update.mutate({ id: note.id, isPinned: !note.isPinned })}
                        active={note.isPinned}
                      >
                        {note.isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                      </IconButton>
                      <IconButton label="Edit note" onClick={() => startEdit(note)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        label="Delete note"
                        destructive
                        onClick={() => remove.mutate(note.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function IconButton({
  label,
  onClick,
  children,
  active,
  destructive,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'rounded-md p-1.5 transition-colors',
        active
          ? 'text-brand-700 hover:bg-muted'
          : destructive
            ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
